/**
 * One team member, and the four things a superadmin can do to them.
 *
 * Every action here is irreversible from this page — a password the owner no
 * longer knows, an authenticator that no longer works, a session that is gone —
 * so none of them fire on a single click. The row expands an inline
 * `<ConfirmAction/>` naming the account and the consequence first. That confirm
 * step is a second `<tr>` rather than something inside the actions cell: at
 * table width the question is a sentence, and a sentence in the last column of
 * a six-column row wraps into a stripe nobody reads.
 *
 * Three of the four are recoverable by a human with the server's cooperation:
 * a new password can be issued, 2FA can be enrolled again, a deactivated row
 * can be flipped back with a shell. `Delete account` is not — the row and its
 * credential material stop existing — so it alone carries a typed
 * confirmation (`confirmPhrase`, the GitHub pattern). It is the last button in
 * a row of near-identical rows, and the difference between deleting the right
 * account and the wrong one has to be something other than aim.
 *
 * The self-targeting guards are duplicated from the backend on purpose. The
 * server refuses all three on your own account (`_load_other_user` in
 * `app/routes/admin.py`), so disabling the buttons changes no security
 * property; what it changes is that the reason is on screen *before* the click
 * instead of arriving as a 400 afterwards. When the server refuses for a reason
 * the UI cannot know — the last active superadmin, a user deleted in another
 * tab — its own message is what gets rendered, verbatim.
 */
import { useState } from 'react'

import { api } from '../api/client'
import { ROLE_LABEL_KEYS } from '../api/types'
import type {
  AdminPasswordResetResponse,
  AdminUserSummary,
  DeactivateResponse,
  Disable2FAResponse,
  RoleId,
  UserDeleteResponse,
} from '../api/types'
import { useI18n } from '../i18n'
import type { TFunction, TranslationKey } from '../i18n'
import { ConfirmAction } from './ConfirmAction'
import { describeAdminError } from './InviteMemberForm'
import { SecretField, SecretReveal } from './SecretReveal'

/** Columns in the team table; the confirm row spans all of them. */
export const TEAM_COLUMN_COUNT = 6

export type AdminActionId = 'reset-password' | 'disable-2fa' | 'deactivate' | 'delete'

interface ActionSpec {
  label: TranslationKey
  /** Names the account and what happens, in one sentence. Takes `{{email}}`. */
  question: TranslationKey
  detail: TranslationKey
  confirmLabel: TranslationKey
  /** False when the action would be a no-op for this account. */
  applies: (user: AdminUserSummary) => boolean
  /** Why the button is off when `applies` is false; null when it always applies. */
  inapplicable: TranslationKey | null
  /** Set on actions that cannot be undone: the admin must type this first. */
  requiresTypedEmail?: boolean
  /** Carries the destructive tint, so the row reads before it is clicked. */
  destructive?: boolean
}

/**
 * What the delete actually did, in a sentence.
 *
 * Exported because it is said twice, in two places, and must not drift between
 * them: by the row while it is still on screen, and by the page once the row is
 * gone. Spelled out rather than a bare "Deleted" -- an admin who believes the
 * history went with the account is wrong about what they just did, and an
 * unredeemed invite that has stopped working is something a waiting colleague
 * is about to ask them about.
 */
export function describeDeletion(res: UserDeleteResponse, t: TFunction): string {
  // Two clauses, not two halves of a sentence: English inflects each on its own
  // count, so they carry their own plural keys, and the sentence that joins
  // them is one whole key per language rather than glued-together fragments.
  const invites = t('account.team.deleted.invites', { count: res.invites_revoked })
  const audit = t('account.team.deleted.audit', { count: res.audit_entries_anonymised })
  return t('account.team.deleted.summary', { email: res.email, invites, audit })
}

export const ADMIN_ACTIONS: Record<AdminActionId, ActionSpec> = {
  'reset-password': {
    label: 'account.team.resetPassword.label',
    question: 'account.team.resetPassword.question',
    detail: 'account.team.resetPassword.detail',
    confirmLabel: 'account.team.resetPassword.confirm',
    applies: () => true,
    inapplicable: null,
  },
  'disable-2fa': {
    label: 'account.team.disable2fa.label',
    question: 'account.team.disable2fa.question',
    detail: 'account.team.disable2fa.detail',
    confirmLabel: 'account.team.disable2fa.confirm',
    applies: (user) => user.totp_enabled,
    inapplicable: 'account.team.disable2fa.inapplicable',
  },
  deactivate: {
    label: 'account.team.deactivate.label',
    question: 'account.team.deactivate.question',
    detail: 'account.team.deactivate.detail',
    confirmLabel: 'account.team.deactivate.confirm',
    applies: (user) => user.is_active,
    inapplicable: 'account.team.deactivate.inapplicable',
  },
  delete: {
    label: 'account.team.delete.label',
    question: 'account.team.delete.question',
    detail: 'account.team.delete.detail',
    confirmLabel: 'account.team.delete.confirm',
    // Always applicable: a deactivated account is the usual thing to delete.
    applies: () => true,
    inapplicable: null,
    requiresTypedEmail: true,
    destructive: true,
  },
}

const ORDER: AdminActionId[] = ['reset-password', 'disable-2fa', 'deactivate', 'delete']

export interface TeamMemberRowProps {
  user: AdminUserSummary
  /** This row is the signed-in superadmin: the backend refuses all three. */
  isSelf: boolean
  /** The account changed server-side; the list is now stale. */
  onChanged: () => void
  /**
   * The account is gone. Handed up because this row is the thing that was
   * destroyed: whatever it renders about the outcome unmounts with it, so the
   * confirmation has to be said by something that outlives it.
   */
  onDeleted?: (result: UserDeleteResponse) => void
}

export function TeamMemberRow({ user, isSelf, onChanged, onDeleted }: TeamMemberRowProps) {
  const { t } = useI18n()
  const [confirming, setConfirming] = useState<AdminActionId | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** An admin-issued temporary password, held until dismissed by hand. */
  const [temporary, setTemporary] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  function ask(action: AdminActionId) {
    setError(null)
    setNotice(null)
    setConfirming(action)
  }

  async function run(action: AdminActionId, typed: string) {
    setPending(true)
    setError(null)
    try {
      if (action === 'reset-password') {
        const res = await api.post<AdminPasswordResetResponse>(
          `/api/v1/admin/users/${user.id}/reset-password`,
        )
        // Held in state, not re-fetchable: the plaintext exists in this
        // response and nowhere else, in this app or in the database.
        setTemporary(res.temporary_password)
      } else if (action === 'disable-2fa') {
        const res = await api.post<Disable2FAResponse>(
          `/api/v1/admin/users/${user.id}/disable-2fa`,
        )
        // Not "from their profile": on an account carrying `totp_required` the
        // next login answers `enrollment_required` and lands them on the forced
        // enrolment screen, never on /profile. And their current session has
        // just been revoked, so "next time they sign in" is now.
        setNotice(t('account.team.disable2fa.notice', { email: res.email }))
      } else if (action === 'deactivate') {
        const res = await api.post<DeactivateResponse>(
          `/api/v1/admin/users/${user.id}/deactivate`,
        )
        setNotice(
          t('account.team.deactivate.notice', {
            email: user.email,
            active: String(res.is_active),
          }),
        )
      } else {
        // `typed`, not `user.email`: the server re-checks the confirmation
        // against the row it is about to destroy, and re-deriving it here from
        // the same props that rendered the row would confirm only what this
        // client already believed. A `users.id` does not reliably name an
        // account on its own -- SQLite reuses the rowid of a deleted row, so a
        // list rendered before a deletion can point this button at whichever
        // account later took that id. Sending what the admin read and typed is
        // what lets the server answer 409 instead of destroying the wrong one.
        const res = await api.del<UserDeleteResponse>(`/api/v1/admin/users/${user.id}`, {
          confirm_email: typed,
        })
        // Both: the page takes the row away and keeps the sentence, and the
        // local copy is what a caller without a handler (a row rendered on its
        // own) still shows rather than nothing.
        setNotice(describeDeletion(res, t))
        onDeleted?.(res)
      }
      setConfirming(null)
      onChanged()
    } catch (err) {
      // Whatever the server said — "cannot deactivate the last active
      // superadmin" is the whole answer, and paraphrasing it loses it.
      setError(describeAdminError(err, t))
      setConfirming(null)
    } finally {
      setPending(false)
    }
  }

  const spec = confirming ? ADMIN_ACTIONS[confirming] : null
  // `isSelf` alone is enough to expand: the reason those three buttons are off
  // has to be readable, not hidden in a `title` a touch device never shows.
  const expanded =
    isSelf || spec !== null || error !== null || notice !== null || temporary !== null

  return (
    <>
      <tr className="border-b border-border/60 align-middle">
        <td className="px-3 py-2 text-sm text-fg">
          <span className="break-all">{user.email}</span>
          {isSelf && <span className="ml-2 text-xs text-muted">{t('account.team.you')}</span>}
        </td>
        {/* The role is product vocabulary (成员 / 超级管理员), not a wire
            value to be shown raw: it reads translated on the profile page and
            in the invite form's picker, so it reads translated here too. A
            role this build has never heard of falls back to the id the server
            sent, exactly as `AccountSummary` does — an unknown role is the one
            thing an admin most needs to see verbatim. */}
        <td className="px-3 py-2 text-sm text-muted">
          {ROLE_LABEL_KEYS[user.role as RoleId]
            ? t(ROLE_LABEL_KEYS[user.role as RoleId])
            : user.role}
        </td>
        <td className="px-3 py-2">
          <Flag
            label={t('account.team.col.active')}
            on={user.is_active}
            tone={user.is_active ? 'good' : 'bad'}
          />
        </td>
        <td className="px-3 py-2">
          {/* "Off (required)" is a different account state from "Off": the
              first is locked out of the product until it enrols, the second is
              a row that predates the mandate and may stay as it is. The admin
              deciding what to do needs to be able to tell them apart. */}
          <Flag
            label={t('domain.twoFactorShort')}
            on={user.totp_enabled}
            tone={user.totp_enabled ? 'good' : 'warn'}
            onText={t('account.team.flag.on')}
            offText={t(
              user.totp_required ? 'account.team.flag.offRequired' : 'account.team.flag.off',
            )}
          />
        </td>
        <td className="px-3 py-2">
          <Flag
            label={t('account.team.col.mustChangePassword')}
            on={user.must_change_password}
            tone={user.must_change_password ? 'warn' : 'neutral'}
          />
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {ORDER.map((id) => {
              const action = ADMIN_ACTIONS[id]
              const applicable = action.applies(user)
              const disabled = isSelf || !applicable || pending
              return (
                <button
                  key={id}
                  type="button"
                  disabled={disabled}
                  onClick={() => ask(id)}
                  title={
                    isSelf
                      ? t('account.team.selfTitle')
                      : action.inapplicable
                        ? t(action.inapplicable)
                        : ''
                  }
                  // Four buttons of equal weight in a row of near-identical
                  // rows is how the wrong one gets pressed. The three that a
                  // human can recover from stay quiet; the one that ends the
                  // account borrows the colour this app already uses for a
                  // down service, so it reads as different BEFORE the click
                  // rather than only in the prompt that follows it.
                  className={`rounded border px-1.5 py-0.5 text-xs disabled:cursor-not-allowed
                              disabled:opacity-50 ${
                                action.destructive
                                  ? 'border-down/60 text-down hover:bg-down/10'
                                  : 'border-border text-muted hover:text-fg'
                              }`}
                >
                  {t(action.label)}
                </button>
              )
            })}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="border-b border-border/60">
          <td colSpan={TEAM_COLUMN_COUNT} className="px-3 pb-3">
            <div className="space-y-2">
              {isSelf && (
                <p className="text-xs text-muted">{t('account.team.selfNote')}</p>
              )}

              {spec && (
                <ConfirmAction
                  // Keyed by action: the typed-confirmation field lives in
                  // ConfirmAction's own state, and switching from Delete to
                  // Deactivate and back must not arrive pre-filled.
                  key={confirming as AdminActionId}
                  question={t(spec.question, { email: user.email })}
                  detail={t(spec.detail)}
                  confirmLabel={t(spec.confirmLabel)}
                  confirmPhrase={spec.requiresTypedEmail ? user.email : undefined}
                  pending={pending}
                  onConfirm={(typed) => void run(confirming as AdminActionId, typed)}
                  onCancel={() => setConfirming(null)}
                />
              )}

              {error && (
                <p role="alert" className="rounded-md bg-down/10 px-3 py-2 text-sm text-down">
                  {error}
                </p>
              )}

              {notice && (
                <p className="rounded-md bg-healthy/10 px-3 py-2 text-sm text-healthy">{notice}</p>
              )}

              {temporary && (
                <SecretReveal
                  title={t('account.team.tempPassword.title', { email: user.email })}
                  warning={t('account.team.tempPassword.warning')}
                  onDismiss={() => setTemporary(null)}
                >
                  <SecretField label={t('account.team.tempPassword.label')} value={temporary} />
                </SecretReveal>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * A yes/no cell whose meaning is in its accessible name.
 *
 * "Yes" on its own is unreadable out of column context, and colour alone is
 * not a label — so the state is spelled out for assistive tech and the tint
 * only reinforces it.
 */
function Flag({
  label,
  on,
  tone,
  onText,
  offText,
}: {
  /** Already translated: the row owns the wording, this owns the shape. */
  label: string
  on: boolean
  tone: 'good' | 'warn' | 'bad' | 'neutral'
  onText?: string
  offText?: string
}) {
  const { t } = useI18n()
  const text = on ? (onText ?? t('account.team.flag.yes')) : (offText ?? t('account.team.flag.no'))
  const styles: Record<string, string> = {
    good: 'bg-healthy/15 text-healthy',
    warn: 'bg-degraded/15 text-degraded',
    bad: 'bg-down/15 text-down',
    neutral: 'bg-unknown/15 text-muted',
  }
  return (
    <span
      aria-label={t('account.team.flag.aria', { label, state: text.toLowerCase() })}
      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${styles[tone]}`}
    >
      {text}
    </span>
  )
}
